import { animate, query, stagger, style, transition, trigger } from '@angular/animations';
import { ChangeDetectorRef, Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from "@angular/common";
import { MainnavService, ViewType } from "../mainNav/mainnav.service";
import { CoreLogService } from '../../services/log.service';
import { WebShopService } from '../../services/webShop.service';
import { Subscription } from 'rxjs';
import { InternationalisationService } from '../../forked/angular-internationalisation';
import { OrderModeSelectorService } from './orderModeSelector.service';
import { RouterHelperService } from '../../services/routerHelper.service';
import { OverlaySpinnerService } from '../overlayspinner/overlaySpiner.service';
import { InHouseOrder } from '../../shared/models';

@Component({
  selector: 'orderModeSelector',
  templateUrl: './orderModeSelector.component.html',
  styleUrls: ['./orderModeSelector.component.scss'],
  providers: [OrderModeSelectorService, OverlaySpinnerService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger("serviceButtonEntering", [
      transition("false => true", [
        query(".service-button", [
          style({ transform: "scale(0)" }),
          stagger(100, [
            animate("400ms ease-out", style({ transform: "scale(1.1)" })),
            animate("100ms ease-in-out", style({ transform: "scale(1)" }))
          ])
        ])
      ])
    ]),
  ]
})

export class OrderModeSelectorComponent implements OnInit, OnDestroy {
  public isSmallView: boolean = false;
  public isLoaded: boolean = false;
  private _langSubscriber: Subscription;
  private _WebshopSettingsInHouseOrderSubscriber: Subscription;
  private tableQrCode: string;

  constructor(private _location: Location
    , private _router: Router
    , private _changeDetectorRef: ChangeDetectorRef
    , private _coreLogService: CoreLogService
    , private _mainnavService: MainnavService
    , private _activatedRoute: ActivatedRoute
    , private internationalisationService: InternationalisationService
    , private _routerHelperService: RouterHelperService
    , private _webShopService: WebShopService
    , private _overlaySpinnerService: OverlaySpinnerService
    , private _orderModeSelectorService: OrderModeSelectorService
  ) {
    this.isSmallView = this._mainnavService.viewType === ViewType.Small;
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("OrderModeComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  ngOnInit() {
    const me = this;

    const routeTargetData = me._activatedRoute.snapshot.data["routeTargetData"];
    if (routeTargetData) {
      me.tableQrCode = routeTargetData["tablecode"];
    }
    // es wurde falsch aufgerufen, control kann sich nicht darstellen
    if (!me.tableQrCode) {
      me._router.navigate(["/"]); // back to home
      return; // exit here
    }

    me._WebshopSettingsInHouseOrderSubscriber = me._orderModeSelectorService.ObserveWebshopSettingsInHouseOrderChanges.subscribe(async () => {
      if (await me.bindWebshopSettingsInHouseOrder())
        me.PushChanges();
    });

    me._langSubscriber = me.internationalisationService.localeChanged.subscribe(() => {
      me.PushChanges();
    });

    me.bindWebshopSettingsInHouseOrder().then(async () => {
      me.isLoaded = true;
      me.PushChanges();
    }).catch(() => {
      me._coreLogService.error("OrderModeSelectorComponent.ngOnInit", "bindWebshopSettingsInHouseOrder fails");
      me.isLoaded = true;
      me.PushChanges(); // eventuell um nichts zu zeichenen?
    });
  }

  public WebshopSettingsInHouseOrder: InHouseOrder = null;
  /** ermittelt die aktuellen InhOuseOrder Einstellungen */
  private async bindWebshopSettingsInHouseOrder(): Promise<boolean> {
    const me = this;
    try {
      me.WebshopSettingsInHouseOrder = await me._orderModeSelectorService.GetWebshopSettingsInHouseOrder();
      return true;
    } catch (error) {
      me._coreLogService.error("OrderModeSelectorComponent.bindWebshopSettingsInHouseOrder", "catch", error);
      return false;
    }
  }

  public get hasImagePhoto(): boolean {
    const me = this;
    if (me.WebshopSettingsInHouseOrder && me.WebshopSettingsInHouseOrder.grouporder)
      return me.WebshopSettingsInHouseOrder.grouporder.last_image_update_at_ymd > 0;
    else
      return false;
  }

  public get imagePhoto(): string {
    const me = this;
    if (me.WebshopSettingsInHouseOrder && me.WebshopSettingsInHouseOrder.grouporder)
      return me.WebshopSettingsInHouseOrder.grouporder.image_photo;
    else
      return "";
  }
  public get useWellcomeLabel(): boolean {
    const me = this;
    if (me.WebshopSettingsInHouseOrder && me.WebshopSettingsInHouseOrder.grouporder)
      return me.WebshopSettingsInHouseOrder.grouporder.use_welcome_text;
    else
      return false;
  }
  public get wellcomeLabel(): string {
    const me = this;
    if (me.WebshopSettingsInHouseOrder && me.WebshopSettingsInHouseOrder.grouporder)
      return me.WebshopSettingsInHouseOrder.grouporder.welcome_text_caption;
    else
      return "";
  }

  public get welcomeSubtext(): string {
    const me = this;
    if (me.WebshopSettingsInHouseOrder && me.WebshopSettingsInHouseOrder.grouporder)
      return me.WebshopSettingsInHouseOrder.grouporder.welcome_text_description;
    else
      return "";
  }

  public get singleOrderTitle(): string {
    const me = this;
    if (me.WebshopSettingsInHouseOrder && me.WebshopSettingsInHouseOrder.grouporder)
      return me.WebshopSettingsInHouseOrder.grouporder.single_order_label;
    else
      return "";
  }

  public get singleOrderDescription(): string {
    const me = this;
    if (me.WebshopSettingsInHouseOrder && me.WebshopSettingsInHouseOrder.grouporder)
      return me.WebshopSettingsInHouseOrder.grouporder.single_order_subtext;
    else
      return "";
  }

  public get groupOrderTitle(): string {
    const me = this;
    if (me.WebshopSettingsInHouseOrder && me.WebshopSettingsInHouseOrder.grouporder)
      return me.WebshopSettingsInHouseOrder.grouporder.group_order_label;
    else
      return "";
  }

  public get groupOrderDescription(): string {
    const me = this;
    if (me.WebshopSettingsInHouseOrder && me.WebshopSettingsInHouseOrder.grouporder)
      return me.WebshopSettingsInHouseOrder.grouporder.group_order_subtext;
    else
      return "";
  }

  /** if single type selected navigate to menu group*/
  public navigateToSingleOrder(): void {
    const me = this;

    //! todo zu dem dialog mit dem eigaben des namens, oder sofortige anledung ohne namen bei CDS?
    const askUserName = true;
    if (askUserName) {
      me._routerHelperService.setRouteTargetDataByKey("tablecode", me.tableQrCode);
      me._router.navigate(["/inHouseUserNameRequest"]);
    } else {
      me._overlaySpinnerService.ShowLayoutSpinner();
      me._webShopService.submitTableCodeData(me.tableQrCode, "").then(() => {
        me._overlaySpinnerService.HideLayoutSpinner();
      }).catch((error) => {
        me._overlaySpinnerService.HideLayoutSpinner();
        me._coreLogService.error("QrcodeScannerComponent.navigateToSingleOrder", "webShopService.submitQRCodeData(catch)", me.tableQrCode, error);
      });
    }
  }

  /**navigate to groupOrder */
  public navigationToGroupOrder(): void {
    const me = this;
    me._routerHelperService.setRouteTargetDataByKey("tablecode", me.tableQrCode);
    me._router.navigate(["/groupOrderCreateGroup"]);
  }

  /** navigate back by swipe from left to right */
  public SwiperNavigateBack() {
    const me = this;
    if (me.isSmallView == true) {
      me.navigateBack();
    }
  }
  /** navigate back */
  public navigateBack(): void {
    this._location.back();
  }

  ngOnDestroy(): void {
    const me = this;
    if (me._langSubscriber) {
      me._langSubscriber.unsubscribe();
    } if (me._WebshopSettingsInHouseOrderSubscriber) {
      me._WebshopSettingsInHouseOrderSubscriber.unsubscribe();
    }
  }
}
